import React, { useState, useEffect } from 'react';
import { X, Globe } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: t('nav.portfolio'), id: 'portfolio' },
    { label: t('nav.proceso'), id: 'proceso' },
    { label: t('nav.testimonios'), id: 'testimonios' },
    { label: t('nav.contacto'), id: 'contacto' }
  ];

  return (
    <>
      <nav className="fixed top-4 left-0 right-0 z-50 px-4 md:px-6">
        {/* Contenedor centrado con efecto ondulado */}
        <div 
          className={`max-w-4xl mx-auto bg-white backdrop-blur-md rounded-full transition-all duration-300 border border-gray-200/60 ${
            isScrolled 
              ? 'shadow-[0_8px_30px_rgb(0,0,0,0.12)]' 
              : 'shadow-[0_4px_20px_rgb(0,0,0,0.08)]'
          }`}
        >
          <div className="px-4 md:px-6 py-2.5">
            <div className="flex items-center justify-between gap-3">
              {/* Logo */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <img
                  src="/jhon_cesar.jpg"
                  alt="Jhon Cesar"
                  className="w-9 h-9 md:w-8 md:h-8 rounded-full object-cover shadow-md"
                />
                <span className="hidden md:block font-bold text-base tracking-tight">Jhon Cesar</span>
              </div>

              {/* Desktop Navigation - Centrado */}
              <div className="hidden lg:flex items-center gap-6">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-gray-600 hover:text-black transition-colors duration-200 font-normal text-sm"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Mobile: ES + Contacto */}
              <div className="flex lg:hidden items-center gap-2 flex-1 justify-center">
                {/* Language Selector Mobile */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors duration-200" onClick={() => setLang(lang === 'es' ? 'en' : 'es')}>
                  <span className="font-medium text-sm">{t('nav.langLabel')}</span>
                </div>

                {/* CTA Button Mobile */}
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="bg-black text-white px-6 py-2 rounded-full hover:bg-gray-900 transition-all duration-300 font-medium text-sm shadow-md"
                >
                  {t('nav.contacto')}
                </button>
              </div>

              {/* Desktop: Language + CTA */}
              <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
                {/* Language Selector Desktop */}
                <div className="flex items-center gap-1.5 text-gray-600 cursor-pointer hover:text-black transition-colors duration-200" onClick={() => setLang(lang === 'es' ? 'en' : 'es')}>
                  <Globe size={16} />
                  <span className="font-normal text-sm">{t('nav.langLabel')}</span>
                </div>

                {/* CTA Button Desktop */}
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="bg-black text-white px-5 py-2 rounded-full hover:bg-gray-900 transition-all duration-300 hover:scale-[1.02] font-medium text-sm shadow-md"
                >
                  {t('nav.hablemos')}
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-1.5 flex-shrink-0 z-[60]"
                aria-label="Toggle menu"
              >
                <div className="flex flex-col gap-1">
                  <span className={`w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
                  }`}></span>
                  <span className={`w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}></span>
                  <span className={`w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${
                    isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
                  }`}></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Full Screen Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>

        {/* Menu Content */}
        <div 
          className={`absolute top-0 left-0 right-0 bg-white rounded-b-[40px] shadow-2xl transition-transform duration-500 ${
            isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <img
                src="/jhon_cesar.jpg"
                alt="Jhon Cesar"
                className="w-10 h-10 rounded-full object-cover shadow-md"
              />
              <span className="font-bold text-lg">Jhon Cesar</span>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
            >
              <X size={20} className="text-gray-700" />
            </button>
          </div>

          {/* Menu Items */}
          <div className="px-6 py-8 space-y-2">
            {navItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left py-4 px-6 text-gray-700 hover:text-black hover:bg-gray-50 rounded-2xl transition-all duration-200 font-medium text-lg"
                style={{
                  animationDelay: `${index * 50}ms`
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Menu Footer */}
          <div className="px-6 pb-8 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 rounded-full text-gray-600 flex-1 justify-center cursor-pointer hover:bg-gray-200 transition-colors duration-200" onClick={() => setLang(lang === 'es' ? 'en' : 'es')}>
                <Globe size={18} />
                <span className="font-medium text-sm">{lang === 'es' ? 'Español' : 'English'}</span>
              </div>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-black text-white px-8 py-2.5 rounded-full hover:bg-gray-900 transition-all duration-300 font-medium text-sm shadow-md flex-1"
              >
                {t('nav.hablemos')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;